import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Edit3, Check, X, Shield, Filter, Search, FileDown, Sparkles } from "lucide-react";

const ACTION_META = {
  "time_entry.edit":     { label: "EDIT",     Icon: Edit3, cls: "bg-warn text-ink" },
  "time_entry.approve":  { label: "APPROVE",  Icon: Check, cls: "bg-good text-white" },
  "time_entry.reject":   { label: "REJECT",   Icon: X,     cls: "bg-bad text-white" },
};

function fmtValue(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    // ISO date? show local
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      try { return new Date(v).toLocaleString([], { dateStyle: "short", timeStyle: "medium" }); } catch { return v; }
    }
    return v;
  }
  return JSON.stringify(v);
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [expanded, setExpanded] = useState(new Set());
  const [diffOpen, setDiffOpen] = useState(null); // audit row selected for the modal diff
  const [premium, setPremium] = useState(null); // null loading, {active, paid, auto_waived}

  const load = useCallback(async () => {
    const url = actionFilter === "all" ? "/audit-logs" : `/audit-logs?action=${actionFilter}`;
    const [logs, billing] = await Promise.all([api.get(url), api.get("/billing/status").catch(() => null)]);
    setRows(logs.data);
    setPremium(billing?.data?.pro || { active: false });
  }, [actionFilter]);
  useEffect(() => { load(); }, [load]);

  const downloadSignedPdf = () => {
    const params = new URLSearchParams();
    if (actionFilter !== "all") params.set("action", actionFilter);
    window.open(`${API}/audit-logs/report.pdf?${params.toString()}`, "_blank");
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    // Broadened full-text search: actor, action, entity, note + before/after diff JSON.
    const diffBlob = JSON.stringify(r.diff || {}) + JSON.stringify(r.before || {}) + JSON.stringify(r.after || {});
    const hay = `${r.actor_email || ""} ${r.action || ""} ${r.entity_type || ""} ${r.entity_id || ""} ${r.note || ""} ${diffBlob}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [rows, q]);

  const toggle = (id) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id); else n.add(id);
    setExpanded(n);
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">§ AUDIT TRAIL</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Immutable record of every approve, reject, and edit — for DOL compliance and internal review.
          </p>
        </div>
        {premium?.active ? (
          <button data-testid="download-signed-pdf" onClick={downloadSignedPdf} className="hard-btn px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <FileDown className="w-4 h-4" /> Download Signed PDF
          </button>
        ) : (
          <Link data-testid="upgrade-for-signed-pdf" to="/app/billing" className="border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white">
            <Sparkles className="w-4 h-4" /> Signed PDF · Pro
          </Link>
        )}
      </div>

      <div className="hard-card p-4 mb-6 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4" />
          <input data-testid="audit-search" placeholder="Search by actor, entity, or note…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 font-mono text-sm bg-transparent focus:outline-none" />
        </div>
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
          <Filter className="w-4 h-4" />
          <label>Action</label>
          <select data-testid="audit-action-filter" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="border-2 border-ink px-2 py-1 bg-white">
            <option value="all">All</option>
            <option value="time_entry.approve">Approve</option>
            <option value="time_entry.reject">Reject</option>
            <option value="time_entry.edit">Edit</option>
          </select>
        </div>
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="audit-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="w-8"></th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">When</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Actor</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Action</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Entity</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Changed Fields</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Note</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No audit events match.</td></tr>}
            {filtered.map((r, i) => {
              const meta = ACTION_META[r.action] || { label: r.action.toUpperCase(), Icon: Shield, cls: "bg-muted text-ink" };
              const isOpen = expanded.has(r.id);
              const changedFields = Object.keys(r.diff || {});
              return (
                <Fragment key={r.id}>
                  <tr className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`audit-row-${r.id}`}>
                    <td className="px-2 py-3 border-t border-ink/20 text-center">
                      <button data-testid={`audit-toggle-${r.id}`} onClick={() => toggle(r.id)} aria-label="Expand" className="hover:bg-ink hover:text-white p-1">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20">
                      <div>{new Date(r.created_at).toLocaleDateString()}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20">
                      <div className="font-semibold">{r.actor_name || r.actor_email}</div>
                      <div className="text-[10px] text-muted-foreground">{r.actor_email}</div>
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest ${meta.cls}`}>
                        <meta.Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20 text-[11px]">
                      <div className="uppercase text-muted-foreground tracking-widest">{r.entity_type}</div>
                      <div className="font-mono">{r.entity_id?.slice(-8)}</div>
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20 text-xs">
                      {changedFields.length > 0
                        ? <button data-testid={`audit-diff-btn-${r.id}`} onClick={() => setDiffOpen(r)} className="inline-flex items-center gap-1 px-2 py-0.5 border-2 border-ink bg-warn/20 text-[10px] uppercase tracking-widest font-black hover:bg-ink hover:text-white">
                            {changedFields.length} field{changedFields.length === 1 ? "" : "s"} · Diff →
                          </button>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20 italic text-muted-foreground max-w-xs">{r.note || "—"}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-ink/5" data-testid={`audit-detail-${r.id}`}>
                      <td></td>
                      <td colSpan={6} className="px-4 py-5 border-t border-ink/20">
                        {changedFields.length === 0 ? (
                          <div className="font-mono text-xs text-muted-foreground">No field changes captured.</div>
                        ) : (
                          <table className="w-full font-mono text-xs">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                <th className="text-left px-2 py-1 border-b border-ink/30 w-1/4">Field</th>
                                <th className="text-left px-2 py-1 border-b border-ink/30">Before</th>
                                <th className="text-left px-2 py-1 border-b border-ink/30">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {changedFields.map((k) => (
                                <tr key={k}>
                                  <td className="px-2 py-2 align-top font-semibold">{k}</td>
                                  <td className="px-2 py-2 align-top">
                                    <span className="inline-block bg-bad/10 border border-bad/30 px-2 py-1 text-bad line-through decoration-bad/50">{fmtValue(r.diff[k].before)}</span>
                                  </td>
                                  <td className="px-2 py-2 align-top">
                                    <span className="inline-block bg-good/10 border border-good/30 px-2 py-1 text-good">{fmtValue(r.diff[k].after)}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-xs text-muted-foreground font-mono">
        Showing latest {filtered.length} events. Audit records are immutable — writes only.
      </div>

      {/* Full-screen Diff Viewer overlay */}
      {diffOpen && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center z-50 p-4" data-testid="diff-modal" onClick={() => setDiffOpen(null)}>
          <div className="bg-white border-4 border-ink w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-hard-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-ink text-white border-b-2 border-ink px-6 py-4 flex items-center justify-between">
              <div>
                <div className="overline text-warn">§ AUDIT DIFF · {diffOpen.action?.toUpperCase()}</div>
                <div className="font-heading font-black text-2xl">{diffOpen.actor_email || diffOpen.actor_name} · {new Date(diffOpen.created_at).toLocaleString()}</div>
                <div className="font-mono text-[10px] text-white/70 mt-1">{diffOpen.entity_type} · {diffOpen.entity_id}</div>
              </div>
              <button onClick={() => setDiffOpen(null)} data-testid="diff-modal-close" className="border-2 border-white text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white hover:text-ink">Close ×</button>
            </div>
            <div className="p-6">
              {diffOpen.note && <div className="border-2 border-warn bg-warn/10 p-3 font-mono text-xs mb-4">📝 {diffOpen.note}</div>}
              <div className="flex items-center gap-2 mb-4">
                <button
                  data-testid="diff-copy-json"
                  onClick={() => {
                    const payload = { id: diffOpen.id, action: diffOpen.action, actor: diffOpen.actor_email, at: diffOpen.created_at, entity_type: diffOpen.entity_type, entity_id: diffOpen.entity_id, note: diffOpen.note, diff: diffOpen.diff, before: diffOpen.before, after: diffOpen.after };
                    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                    toast.success("Audit JSON copied to clipboard");
                  }}
                  className="border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-2"
                >📋 Copy JSON</button>
                <button
                  data-testid="diff-save-pdf"
                  onClick={() => {
                    const w = window.open("", "_blank");
                    if (!w) { toast.error("Popup blocked — allow popups then retry"); return; }
                    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) =>
                      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
                    const rows = Object.entries(diffOpen.diff || {}).map(([k, v]) =>
                      `<tr><td style="padding:6px;border:1px solid #000;font-weight:700">${esc(k)}</td><td style="padding:6px;border:1px solid #000;background:#fee;color:#900;text-decoration:line-through">${esc(fmtValue(v.before))}</td><td style="padding:6px;border:1px solid #000;background:#efe;color:#080">${esc(fmtValue(v.after))}</td></tr>`
                    ).join("");
                    w.document.write(`<!doctype html><html><head><title>Audit Diff · ${esc(diffOpen.id)}</title><style>body{font-family:ui-monospace,monospace;padding:24px;color:#000}h1{font-size:18px;margin:0 0 4px}h2{font-size:12px;font-weight:400;color:#666;margin:0 0 16px}table{border-collapse:collapse;width:100%;font-size:12px}th{background:#000;color:#fff;padding:8px;border:1px solid #000;text-align:left}.foot{margin-top:20px;font-size:10px;color:#666}</style></head><body><h1>Audit Diff · ${esc(diffOpen.action?.toUpperCase())}</h1><h2>${esc(diffOpen.actor_email)} · ${esc(new Date(diffOpen.created_at).toLocaleString())} · ${esc(diffOpen.entity_type)} / ${esc(diffOpen.entity_id)}</h2>${diffOpen.note ? `<div style="border:2px solid #e6b000;background:#fff8dc;padding:8px;margin-bottom:16px;font-size:11px">📝 ${esc(diffOpen.note)}</div>` : ""}<table><thead><tr><th>Field</th><th>Old Value</th><th>New Value</th></tr></thead><tbody>${rows}</tbody></table><div class="foot">Checkmate Payroll · Immutable audit record · Generated ${esc(new Date().toLocaleString())}</div><script>window.print();</script></body></html>`);
                    w.document.close();
                  }}
                  className="border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-2"
                >🖨 Save as PDF</button>
              </div>
              {Object.keys(diffOpen.diff || {}).length === 0 ? (
                <div className="font-mono text-xs text-muted-foreground italic">No field-level changes captured.</div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(diffOpen.diff).map(([k, v]) => (
                    <div key={k} className="border-2 border-ink" data-testid={`diff-row-${k}`}>
                      <div className="bg-ink text-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest">{k}</div>
                      <div className="grid grid-cols-2 divide-x-2 divide-ink">
                        <div className="p-3 bg-bad/10">
                          <div className="overline mb-2 text-bad">Old Value</div>
                          <div className="font-mono text-sm text-bad line-through decoration-bad/40 break-all">{fmtValue(v.before)}</div>
                        </div>
                        <div className="p-3 bg-good/10">
                          <div className="overline mb-2 text-good">New Value</div>
                          <div className="font-mono text-sm text-good break-all">{fmtValue(v.after)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
